/**
 * Single source of truth for every Montessori-koulu article.
 * Used by the journal index (listing + grouping) and by BlogPost
 * (related-reading, hero image, FAQs), so a new article only needs
 * to be added here.
 */

import { Baby, BookOpen, Compass, CookingPot, Home, Shirt, Triangle, type LucideIcon } from "lucide-react";

export type PostHref =
  | "/montessori-koulu/nordic-montessori-konferenssi-2026"
  | "/montessori-koulu/montessori-suomessa-2026"
  | "/montessori-koulu/lapsuus-muuttuu-digiaika"
  | "/montessori-koulu/pikler-kiipeilyteline-opas"
  | "/montessori-koulu/montessori-kalusteet-kotiin"
  | "/montessori-koulu/kiipeilykolmio-lapselle-ikaopas"
  | "/montessori-koulu/oppimistorni-turvallisuus"
  | "/montessori-koulu/montessori-lattiapatja"
  | "/montessori-koulu/lapsi-keittiossa-ikaopas"
  | "/montessori-koulu/montessori-vaatehuolto-opas"
  | "/montessori-koulu/vanhempien-tarinoita"
  | "/montessori-koulu/montessori-historia-suomi"
  | "/montessori-koulu/hei-koulujarjestelma-historia"
  | "/montessori-koulu/montessori-hei-vertailu"
  | "/montessori-koulu/maria-montessori-elamakerta"
  | "/montessori-koulu/emmi-pikler-elamakerta"
  | "/montessori-koulu/montessori-waldorf-reggio"
  | "/montessori-koulu/pienet-muutokset-kotona-itsenaisyys"
  | "/montessori-koulu/montessori-kotona"
  | "/montessori-koulu/montessori-myytit";

export type CategoryId =
  | "perusteet"
  | "vauva"
  | "taapero"
  | "itsenaisyys"
  | "koti"
  | "motoriikka"
  | "vanhemman-opas";

export const CATEGORIES: ReadonlyArray<{ id: CategoryId; label: string; blurb: string; icon: LucideIcon }> = [
  { id: "perusteet", label: "Montessorin perusteet", blurb: "Mikä Montessori on — historia, periaatteet ja tekijät.", icon: BookOpen },
  { id: "vauva", label: "Vauva 0–12 kk", blurb: "Uni, vapaa liike ja ensimmäinen valmisteltu ympäristö.", icon: Baby },
  { id: "taapero", label: "Taapero 1–3 v", blurb: "Arjen taidot ja keittiön työt taaperon käsissä.", icon: CookingPot },
  { id: "itsenaisyys", label: "Itsenäisyys & arki", blurb: "Pukeutuminen, pienet teot ja itselle huolehtiminen.", icon: Shirt },
  { id: "koti", label: "Kodin ympäristö", blurb: "Valmisteltu ympäristö — kalusteet ja rajattu valikoima.", icon: Home },
  { id: "motoriikka", label: "Liike & motoriikka", blurb: "Pikler ja vapaa kiipeily kehityksen moottorina.", icon: Triangle },
  { id: "vanhemman-opas", label: "Vanhemman oppaat", blurb: "Vertailut, turvallisuuslistat ja perheiden kokemukset.", icon: Compass },
];

/**
 * Reader-facing topic taxonomy. `CategoryId` stays the internal archive
 * grouping (and the hero-image key); `TopicId` is what parents filter by.
 */
export type TopicId = "kotona" | "liike" | "arki" | "leikki" | "ikakaudet" | "turvallisuus";

export const TOPICS: ReadonlyArray<{ id: TopicId; label: string; blurb: string; icon: LucideIcon }> = [
  { id: "kotona", label: "Montessori kotona", blurb: "Valmisteltu ympäristö, lelujen kierto ja lapsen korkeudelle tuotu säilytys.", icon: Home },
  { id: "liike", label: "Liike & motoriset taidot", blurb: "Pikler, kiipeily, tasapaino ja turvallinen liikkuminen sisällä.", icon: Triangle },
  { id: "arki", label: "Arjen taidot", blurb: "Pukeutuminen, ruoanlaitto yhdessä, siivous ja pöydän kattaminen.", icon: CookingPot },
  { id: "leikki", label: "Leikki & oppiminen", blurb: "Avoin leikki, keskittyminen, toisto ja itsenäinen puuhastelu.", icon: BookOpen },
  { id: "ikakaudet", label: "Ikäkaudet", blurb: "Ideoita eri kehitysvaiheisiin — ei tarkistuslista siitä, mitä lapsen pitäisi osata.", icon: Baby },
  { id: "turvallisuus", label: "Turvallisuus & tuotteen valinta", blurb: "Koko, materiaalit, huolto ja valmistajan ohjeiden lukeminen.", icon: Compass },
];

export type AgeGroupId = "0-1" | "1-2" | "2-3" | "3-6";

export const AGE_GROUPS: ReadonlyArray<{ id: AgeGroupId; label: string; blurb: string }> = [
  { id: "0-1", label: "0–1 vuotta", blurb: "Liikkumisen, ympäristön tutkimisen ja arjen rytmien ensimmäiset vaiheet." },
  { id: "1-2", label: "1–2 vuotta", blurb: "Itsenäisen liikkumisen, osallistumisen ja yksinkertaisten arjen tehtävien harjoittelua." },
  { id: "2-3", label: "2–3 vuotta", blurb: "Kasvavaa halua tehdä itse, jäljitellä aikuisia ja osallistua arjen tehtäviin." },
  { id: "3-6", label: "3–6 vuotta", blurb: "Lisää vastuuta, keskittymistä, hienomotoriikkaa ja monivaiheisia arjen tehtäviä." },
];

/** Learning paths — ordered reading sequences shown as "Osa N". */
export type SeriesId = "montessori-kotona-perusteet";

export const SERIES: Record<SeriesId, { label: string; slugs: readonly string[] }> = {
  "montessori-kotona-perusteet": {
    label: "Montessori kotona — perusteet",
    slugs: [
      "montessori-kotona",
      "montessori-kalusteet-kotiin",
      "pienet-muutokset-kotona-itsenaisyys",
      "montessori-vaatehuolto-opas",
      "lapsi-keittiossa-ikaopas",
    ],
  },
};

export type Post = {
  slug: string;
  to: PostHref;
  category: CategoryId;
  /** Reader-facing topic used by the hub filters. */
  topic?: TopicId;
  /** Which developmental stages the article speaks to — never a milestone claim. */
  ageGroups?: readonly AgeGroupId[];
  /** Free-text keywords used by hub search. */
  tags?: readonly string[];
  /** ISO date of the last genuine content change. */
  updatedISO?: string;
  /** Long-form hub cornerstone; surfaced as "Suosittu opas". */
  pillar?: boolean;
  /** Shown in the "Uusi Montessorissa?" starting row. */
  starter?: boolean;
  eyebrow: string;
  title: string;
  shortTitle: string;
  excerpt: string;
  minutes: number;
  age?: string;
  featured?: boolean;
  /** One-sentence "Yhteenveto" box at the end of the article. */
  takeaway?: string;
  /** Rendered as an accessible accordion + FAQPage JSON-LD. */
  faqs?: ReadonlyArray<{ q: string; a: string }>;
  /** Formatted citations shown under "Lähteet". */
  sources?: readonly string[];
};

export const POSTS: ReadonlyArray<Post> = [
  {
    slug: "nordic-montessori-konferenssi-2026",
    to: "/montessori-koulu/nordic-montessori-konferenssi-2026",
    category: "perusteet",
    topic: "kotona",
    ageGroups: ["0-1", "1-2", "2-3", "3-6"],
    tags: [
      "nordic montessori conference",
      "helsingør",
      "ami",
      "maria montessori",
      "montessori sports",
      "konferenssi",
      "2026",
    ],
    updatedISO: "2026-08-23",
    featured: true,
    eyebrow: "Ajankohtaista",
    title:
      "Montessori palaa juurilleen — Pohjoismaiden suuri Montessori-konferenssi kokoontuu Helsingørissä elokuussa 2026",
    shortTitle: "Nordic Montessori Conference 2026",
    excerpt:
      "9. Nordic Montessori Conference kokoontuu 28.–30.8.2026 Helsingøriin, samaan paikkaan, jossa AMI sai alkunsa vuonna 1929. Ohjelma ulottuu vauvaiästä vanhuuteen.",
    minutes: 10,
    takeaway:
      "Helsingørin konferenssi näyttää, ettei Montessori ole vain päiväkotipedagogiikkaa: ohjelma kulkee vauvaiästä liikkeen, arkkitehtuurin ja muistisairaiden hoivan kautta aina nykyajan digitaalisiin haasteisiin.",
    faqs: [
      {
        q: "Milloin ja missä Nordic Montessori Conference 2026 järjestetään?",
        a: "Konferenssi järjestetään 28.–30. elokuuta 2026 Marienlyst Strandhotelissa Helsingørissä Tanskassa. Konferenssikieli on englanti.",
      },
      {
        q: "Miksi juuri Helsingør on tapahtumapaikkana merkittävä?",
        a: "Helsingørissä järjestettiin vuonna 1929 ensimmäinen International Montessori Congress, ja samana vuonna Maria ja Mario Montessori loivat perustan Association Montessori Internationalelle (AMI). Maria ja Mario majoittuivat tuolloin samassa Marienlyst-hotellissa.",
      },
      {
        q: "Kenelle konferenssi on tarkoitettu?",
        a: "Järjestäjän mukaan mukana on Montessori-opettajia, tutkijoita, koulunjohtajia, terveydenhuollon ammattilaisia sekä vanhempia ja muita kasvatuksesta kiinnostuneita eri puolilta maailmaa.",
      },
      {
        q: "Onko ohjelmassa suomalaisia puhujia?",
        a: "Kyllä. Lauantaiaamun ohjelmassa esiintyy AMI:n 0–3-vuotiaiden Montessori-kouluttaja Tiina Suominen, jonka puheenvuoro käsittelee Montessori-ajattelua aivan ensimmäisistä elinvuosista alkaen.",
      },
    ],
    sources: [
      "Nordic Montessori Conference 2026: tapahtuman ohjelma, puhujat ja paikkatiedot (Marienlyst Strandhotel, Helsingør, 28.–30.8.2026).",
      "Association Montessori Internationale (AMI): järjestön perustaminen vuonna 1929 ja Helsingørin ensimmäinen International Montessori Congress.",
      "Montessori Norge: Nordic Montessori Conference järjestetään joka toinen vuosi ja isännyys kiertää Pohjoismaiden kesken.",
    ],
  },
  {
    slug: "montessori-suomessa-2026",
    to: "/montessori-koulu/montessori-suomessa-2026",
    category: "perusteet",
    topic: "kotona",
    ageGroups: ["0-1", "1-2", "2-3", "3-6"],
    tags: [
      "montessori suomessa",
      "montessori koulu",
      "montessori päiväkoti",
      "ami koulutus",
      "helsinki",
      "espoo",
      "salon",
      "halikko",
      "2026",
    ],
    updatedISO: "2026-08-23",
    featured: true,
    eyebrow: "Montessori Suomessa",
    title: "Montessori Suomessa 2026 — pedagogiikka, joka kiinnostaa yhä uusia perheitä",
    shortTitle: "Montessori Suomessa 2026",
    excerpt:
      "Montessori on Suomessa päiväkoteja, kunnallista perusopetusta, yläkoulua ja kansainvälistä opettajankoulutusta. Katso, mitä vuonna 2026 tapahtuu Helsingissä, Espoossa, Salossa ja muualla.",
    minutes: 12,
    takeaway:
      "Montessori Suomessa on vuonna 2026 paitsi päiväkoteja ja leluja myös kunnallista perusopetusta, yläkoulua ja kansainvälistä AMI-opettajankoulutusta — pedagogiikka, joka integroituu suomalaiseen koulutusjärjestelmään.",
    faqs: [
      {
        q: "Onko Montessori Suomessa vain päiväkoteja ja leluja?",
        a: "Ei. Suomessa toimii myös kunnallista Montessori-perusopetusta (Espoo) ja yksityinen Montessori-koulu (Helsinki), ja Helsingissä on myös 7.–9.-luokkien Montessori-linja. Lisäksi järjestetään kansainvälisen AMI-pätevyyden opettajankoulutusta.",
      },
      {
        q: "Miten Montessori eroaa tavallisesta suomalaisesta koulusta?",
        a: "Montessori-koulu noudattaa suomalaista valtakunnallista opetussuunnitelmaa, mutta oppiminen tapahtuu useammin itsenäisen työskentelyn, konkreettisten materiaalien ja lapsen omaan tahtiin etenemisen kautta. Aikuinen valmistelee ympäristön ja havainnoi enemmän kuin ohjaa.",
      },
      {
        q: "Mikä on AMI-koulutus?",
        a: "AMI eli Association Montessori Internationale on Maria Montessorin perustama kansainvälinen järjestö. Suomessa järjestettävät AMI 3–6 ja 6–12 -opettajakoulutukset ovat vuosia kestäviä pätevöitymisohjelmia, jotka päättyvät kansainväliseen AMI-diplomiin.",
      },
      {
        q: "Miten pääsee Montessori-kouluun Suomessa?",
        a: "Helsingin Montessori-kouluun ja Apollon Yhteiskoulun yläkoululinjalle haetaan koulujen omien hakujen kautta. Espoon kunnalliset Montessori-luokat ovat osa kaupungin normaalia kouluhakua Wilman kautta. Tarkat ajankohdat ja paikat kannattaa tarkistaa suoraan koulujen ja kaupunkien sivuilta.",
      },
    ],
    sources: [
      "Helsingin Montessori-koulu: tietoa koulun toiminnasta, oppilasmääristä ja vanhempien infoilloista lukuvuonna 2026–2027.",
      "Espoon kaupunki: kunnallinen kouluhaku 2026 ja Niittykummun sekä Ruusutorpan koulujen Montessori-opetusta koskeva kuvaus.",
      "Helsingin Apollon Yhteiskoulu: 7.–9.-luokkien Montessori-opetuksen haku ja toiminnan kuvaus.",
      "Montessori-päiväkoti Aurinkoleijona: avoimet ovet Salon ja Halikon yksiköissä 14.3.2026.",
      "Suomen Montessoriliitto: luettelo Suomessa toimivista Montessori-päiväkodeista ja -kouluista.",
      "Suomen Montessori-koulutus (AMI 3–6): opettajankoulutuksen aikataulu ja sisältökuvaus vuonna 2026.",
      "Suomen Montessori-instituutti (AMI 6–12): alakouluikäisten opettajankoulutuksen aikataulu ja lähiopetusjaksot Espoossa 2026.",
    ],
  },
  {
    slug: "lapsuus-muuttuu-digiaika",
    to: "/montessori-koulu/lapsuus-muuttuu-digiaika",
    category: "motoriikka",
    topic: "liike",
    ageGroups: ["1-2", "2-3", "3-6"],
    tags: ["ruutuaika", "digisuositukset", "motoriset taidot", "liikunta", "WHO", "PIILO", "kiipeily", "leikki"],
    updatedISO: "2026-08-23",
    featured: true,
    eyebrow: "Näkökulma",
    title: "Lapsuus muuttuu nopeammin kuin lapsi — miksi liikettä tarvitaan digiajassa enemmän kuin koskaan",
    shortTitle: "Liike digiajan lapsuudessa",
    excerpt:
      "Teknologia kehittyy valtavalla nopeudella. Lapsen keho ja aivot eivät. Mitä tuoreet suositukset ja tutkimukset kertovat liikkeestä, ruutuajasta ja siitä, mitä lapsi tarvitsee ympärilleen.",
    minutes: 9,
    takeaway:
      "Kysymys ei ole vain ruutuajan minuuteista vaan siitä, jääkö päivään tilaa liikkeelle, unelle, leikille ja yhdessäololle — ne kehittyvät edelleen vain oikeassa maailmassa.",
    faqs: [
      {
        q: "Kuinka paljon pienen lapsen tulisi liikkua päivässä?",
        a: "WHO suosittelee 1–4-vuotiaille vähintään kolme tuntia erilaista fyysistä aktiivisuutta päivän aikana, ja 3–4-vuotiailla vähintään tunnin tästä tulisi olla reippaampaa tai rasittavampaa liikkumista. Käytännössä tämä tarkoittaa leikkiä, ei harjoittelua.",
      },
      {
        q: "Mitä Suomen uudet digisuositukset sanovat ruutuajasta?",
        a: "Tammikuussa 2026 julkaistuissa kansallisissa vapaa-ajan digisuosituksissa alle 2-vuotiaille ei suositella varsinaista ruutuaikaa, 2–10-vuotiaille enintään tuntia päivässä ja 11–13-vuotiaille enintään kahta tuntia. Alle 13-vuotiaalle ei myöskään suositella omaa älypuhelinta.",
      },
      {
        q: "Onko ruutuaika haitallista motoriselle kehitykselle?",
        a: "Useissa tutkimuksissa runsaampi ruutujen käyttö on yhdistetty heikompaan motoriseen kehitykseen, mutta tutkimusten laatu vaihtelee eikä suoraa syy–seuraussuhdetta voida päätellä. Järkevin johtopäätös on, että kehittyvä keho tarvitsee aikaa kehittyäkseen.",
      },
      {
        q: "Liikkuvatko suomalaislapset tarpeeksi?",
        a: "PIILO-tutkimuksessa 2023 noin 76 prosenttia 4–6-vuotiaista täytti varhaisvuosien fyysisen aktiivisuuden suosituksen. Samalla paikallaanoloa kertyi keskimäärin 9,4 tuntia vuorokaudessa, joten arjen rakenne on muuttumassa.",
      },
    ],
    sources: [
      "Opetushallitus & Terveyden ja hyvinvoinnin laitos (2026): Digisuositukset lapsille ovat valmiit – omia älypuhelimia ei suositella alle 13-vuotiaille.",
      "Opetus- ja kulttuuriministeriö (2024): Pienten lasten liikunnan ilo, fyysinen aktiivisuus ja motoriset taidot Suomessa – PIILO-tutkimuksen tuloksia 2023.",
      "World Health Organization: Guidelines on physical activity, sedentary behaviour and sleep for children under 5 years of age.",
      "World Health Organization (2020): WHO Guidelines on physical activity and sedentary behaviour.",
      "American Academy of Pediatrics (2026): Munzer T. et al., Digital Ecosystems, Children, and Adolescents: Policy Statement, Pediatrics 157(2).",
      "Norwegian Institute of Public Health (2024): Screen use and children and adolescents’ emotional, cognitive and motor development: an umbrella review.",
    ],
  },
  {
    slug: "montessori-kotona",
    to: "/montessori-koulu/montessori-kotona",
    category: "koti",
    topic: "kotona",
    ageGroups: ["0-1", "1-2", "2-3", "3-6"],
    tags: ["montessori kotona", "valmisteltu ympäristö", "eteinen", "keittiö", "lastenhuone", "kylpyhuone", "lelujen kierto"],
    updatedISO: "2026-08-16",
    pillar: true,
    starter: true,
    featured: true,
    eyebrow: "Käytännön opas",
    title: "Montessori kotona — käytännön opas suomalaisille vanhemmille",
    shortTitle: "Montessori kotona — käytännön opas",
    excerpt:
      "Mistä aloittaa, huone kerrallaan: eteinen, keittiö, lastenhuone, kylpyhuone, lelut ja arjen tehtävät — ja mitä ei tarvitse ostaa lainkaan.",
    minutes: 11,
    takeaway:
      "Montessori kotona on ennen kaikkea järjestelyä: kun tavarat ovat lapsen ulottuvilla ja valikoima on rajattu, lapsi pääsee osallistumaan arkeen ilman että aikuinen tekee kaiken puolesta.",
    faqs: [
      {
        q: "Tarvitseeko Montessori-koti erityisiä huonekaluja?",
        a: "Ei tarvitse. Suurin osa muutoksista voidaan tehdä tavallisilla kodin esineillä: matala kori, yksi koukku sopivalla korkeudella, pieni kannu ja hyllyn alin taso riittävät alkuun.",
      },
      {
        q: "Mistä huoneesta kannattaa aloittaa?",
        a: "Siitä, jossa arki takkuaa eniten. Monelle perheelle se on eteinen tai keittiö, koska niissä lapsi haluaa osallistua mutta ei yllä tarvitsemiinsa tavaroihin.",
      },
      {
        q: "Pitääkö kaikkien lelujen olla puuta?",
        a: "Ei. Montessori on ajattelutapa ympäristöstä ja lapsen osallistumisesta, ei materiaalivaatimus. Tärkeämpää on määrän rajaaminen ja se, että lapsi löytää ja palauttaa tavarat itse.",
      },
      {
        q: "Kuinka kauan muutosten tekemiseen menee?",
        a: "Yksi muutos kerrallaan riittää. Monet perheet huomaavat eron jo yhdestä asiasta — esimerkiksi siitä, että lapsen omat kengät ovat omassa korissaan lattiatasolla.",
      },
    ],
  },
  {
    slug: "montessori-myytit",
    to: "/montessori-koulu/montessori-myytit",
    category: "perusteet",
    topic: "leikki",
    tags: ["myytit", "montessori", "puulelut", "rajat", "mielikuvitusleikki"],
    updatedISO: "2026-08-16",
    starter: true,
    featured: true,
    eyebrow: "Montessori-myytit",
    title: "Montessori-myytit — neljä yleistä väärinkäsitystä",
    shortTitle: "Montessori-myytit",
    excerpt:
      "Kalliit puulelut, rajaton vapaus, beige koti ja kielletty mielikuvitusleikki. Neljä sitkeää myyttiä ja se, mistä Montessorissa oikeasti on kyse.",
    minutes: 6,
    takeaway:
      "Montessori on ajattelutapa lapsen ympäristöstä ja osallistumisesta — ei ostoslista, sisustustyyli eikä sääntö siitä, millaisia leikkejä lapsi saa leikkiä.",
  },
  {
    slug: "pienet-muutokset-kotona-itsenaisyys",
    topic: "arki",
    ageGroups: ["1-2", "2-3", "3-6"],
    tags: ["itsenäisyys", "eteinen", "arjen taidot", "pukeutuminen", "valmisteltu ympäristö"],
    updatedISO: "2026-08-16",
    starter: true,
    to: "/montessori-koulu/pienet-muutokset-kotona-itsenaisyys",
    category: "itsenaisyys",
    eyebrow: "Arjen Montessori",
    title: "Pienet muutokset kotona — suuri vaikutus lapsen itsenäisyyteen",
    shortTitle: "Pienet muutokset kotona",
    excerpt:
      "Viisi käytännön askelta, joilla suomalainen perhe kasvattaa lapsen itsenäisyyttä, itseluottamusta ja oppimisen iloa arjen pienillä muutoksilla.",
    minutes: 7,
    age: "1 – 6 v",
    featured: true,
    takeaway:
      "Itsenäisyyttä ei tarvitse opettaa — riittää, että poistat esteitä. Matala koukku, oma tiskiallas ja rajattu valikoima tekevät enemmän kuin tuhat ohjetta.",
    faqs: [
      {
        q: "Pitääkö koko koti muuttaa kerralla?",
        a: "Ei. Aloita yhdestä huoneesta ja yhdestä toiminnosta — esimerkiksi eteisen matalasta koukusta. Pieni muutos kerrallaan on Montessorin periaate myös aikuiselle.",
      },
      {
        q: "Sopiiko Montessori pieneen kotiin?",
        a: "Erinomaisesti. Montessori ei vaadi erillistä lastenhuonetta vaan lapsen mittakaavaisia muutoksia: matala hylly, oma jakkara ja paikka, mihin tavarat laitetaan pois.",
      },
      {
        q: "Miten aloitan, jos lapsi on jo yli 3-vuotias?",
        a: "Ei ole koskaan myöhäistä. Aloita havainnoimalla, mitä lapsi yrittää jo tehdä itse, ja järjestä ympäristö niin, että hän onnistuu siinä ilman apua.",
      },
    ],
    sources: [
      "Lillard, A. S. (2017). Montessori: The Science Behind the Genius (3rd ed.). Oxford University Press.",
      "Montessori, M. (1949). The Absorbent Mind.",
    ],
  },
  {
    slug: "pikler-kiipeilyteline-opas",
    topic: "liike",
    ageGroups: ["0-1", "1-2", "2-3", "3-6"],
    tags: ["pikler", "kiipeilyteline", "kolmio", "motoriikka", "ramppi"],
    updatedISO: "2026-08-16",
    starter: true,
    to: "/montessori-koulu/pikler-kiipeilyteline-opas",
    category: "motoriikka",
    eyebrow: "Pikler-opas",
    title: "Pikler-kiipeilyteline — täydellinen ostajan opas",
    shortTitle: "Pikler-kiipeilyteline: ostajan opas",
    excerpt: "Ikäsuositukset, turvallisuus, mallit ja ostovinkit Pikler-kiipeilytelineen valintaan.",
    minutes: 9,
    age: "Ikä vaihtelee mallista",
    featured: true,
    takeaway:
      "Pikler-kiipeilyteline palvelee lasta useita vuosia — tarkista tuotekohtainen ikäsuositus ja turvallisuustiedot, anna lapsen edetä omaan tahtiin ja lisää ramppi vasta kun lapsi kiipeää itsenäisesti.",
    faqs: [
      {
        q: "Mistä iästä Pikler-kolmiota voi käyttää?",
        a: "Monet lapset alkavat kiinnostua kiipeilystä siinä vaiheessa, kun pystyasento ja liikkuminen kehittyvät nopeasti. Sopiva ajankohta riippuu lapsesta ja käytettävästä tuotteesta — tarkista tuotekohtainen ikäsuositus. Anna lapsen edetä omaan tahtiin, ilman että aikuinen nostaa lasta askelmille.",
      },
      {
        q: "Onko Pikler-kolmio turvallinen?",
        a: "Kun teline on tukevarakenteinen, runko on koivuvaneria ja askelmat on kiinnitetty ruuviliitoksin, kolmio on turvallinen. Aseta se pehmeän pelimaton päälle ja pysy lähellä etenkin ensimmäisinä käyttöviikkoina.",
      },
      {
        q: "Milloin ramppi tai kaari kannattaa lisätä?",
        a: "Ramppi sopii 12–24 kuukauden ikäiselle, kun lapsi hallitsee ryömimisen ja liukuun laskeutumisen. Kiipeilykaari kannattaa lisätä vasta 2–3 vuoden iässä, kun peruskiipeily on jo sujuvaa.",
      },
    ],
    sources: [
      "Pikler, E. (1988). Peaceful Babies — Contented Mothers. Sensory Awareness Foundation.",
      "Adolph, K. E. et al. (2012). How do you learn to walk? Psychological Science, 23(11).",
    ],
  },
  {
    slug: "oppimistorni-turvallisuus",
    topic: "turvallisuus",
    ageGroups: ["1-2", "2-3", "3-6"],
    tags: ["oppimistorni", "turvallisuus", "keittiö", "valvonta"],
    updatedISO: "2026-08-16",
    to: "/montessori-koulu/oppimistorni-turvallisuus",
    category: "vanhemman-opas",
    eyebrow: "Turvallisuus",
    title: "Oppimistornin turvallisuus — mitä vanhemman kannattaa tarkistaa?",
    shortTitle: "Oppimistornin turvallisuus",
    excerpt: "Mitä oppimistornissa kannattaa tarkistaa: vakaus, sijoituspaikka, rakenne, valmistajan ohjeet ja tuotekohtaiset turvallisuustiedot.",
    minutes: 6,
    age: "Ikä vaihtelee mallista",
    featured: true,
    takeaway:
      "Turvallisuus riippuu tuotteesta, lapsesta ja käyttötavasta: lue valmistajan ohjeet, tarkista vakaus ja kunto säännöllisesti äläkä jätä lasta torniin ilman valvontaa.",
    faqs: [
      {
        q: "Mistä iästä oppimistornia voi käyttää?",
        a: "Ikäsuositus vaihtelee mallin ja valmistajan mukaan, joten tarkista se aina kyseisen tuotteen tiedoista. Ikä yksin ei kerro valmiudesta: arvioi myös, pystyykö lapsi käyttämään tuotetta valmistajan tarkoittamalla tavalla.",
      },
      {
        q: "Mitä turvallisuusvaatimuksia oppimistornia koskee?",
        a: "Valmistaja vastaa siitä, että tuote täyttää sitä koskevat soveltuvat turvallisuusvaatimukset. Vaatimukset voivat riippua tuotteen rakenteesta, käyttötarkoituksesta ja luokittelusta, joten tarkista tuotekohtaiset turvallisuustiedot ja valmistajan käyttöohjeet.",
      },
      {
        q: "Voiko lapsi olla tornissa ilman valvontaa?",
        a: "Oppimistorni ei korvaa aikuisen valvontaa. Keittiössä lapsi pääsee aiempaa korkeammalle ja lähemmäs kuumia pintoja ja teräviä esineitä, joten pidä aikuinen lähellä käytön ajan.",
      },
    ],
    sources: [
      "Tuotteen valmistajan käyttöohje ja turvallisuustiedot — ensisijainen tietolähde tuotekohtaisissa kysymyksissä.",
      "Turvallisuus- ja kemikaalivirasto (Tukes): kuluttajaturvallisuutta ja lasten tuotteita koskeva ohjeistus.",
      "Kilpailu- ja kuluttajavirasto (KKV): kuluttajan oikeudet ja tuotetietojen antaminen.",
      "Euroopan unioni: yleinen tuoteturvallisuusasetus (EU) 2023/988 ja leluturvallisuusdirektiivi 2009/48/EY (sovellettavuus riippuu tuotteen luokittelusta).",
    ],
  },
  {
    slug: "montessori-lattiapatja",
    topic: "kotona",
    ageGroups: ["0-1", "1-2", "2-3"],
    tags: ["lattiapatja", "uni", "lastenhuone", "sänky"],
    updatedISO: "2026-08-16",
    to: "/montessori-koulu/montessori-lattiapatja",
    category: "vauva",
    eyebrow: "Uni & huone",
    title: "Montessori-lattiavuode — mitä vanhemman kannattaa tietää?",
    shortTitle: "Montessori-lattiavuode",
    excerpt:
      "Mitä lattiavuoteella tarkoitetaan, miten huone tehdään lapsiturvalliseksi ja miten Montessori-ajattelu eroaa vauvan turvallisen unen suosituksista.",
    minutes: 7,
    age: "0 – 3 v",
    featured: true,
    takeaway:
      "Lattiavuode on Montessori-ajatus saavutettavasta ympäristöstä, ei turvallisuusratkaisu: vauvan nukkumisjärjestelyissä noudata ajantasaisia turvallisen unen suosituksia ja tuotteen valmistajan ohjeita.",
    faqs: [
      {
        q: "Milloin lattiavuodetta voi harkita?",
        a: "Ajankohta riippuu lapsen kehityksestä, nukkumisjärjestelystä, käytettävästä tuotteesta ja paikallisista turvallisen unen suosituksista. Yleispätevää ikärajaa ei kannata olettaa.",
      },
      {
        q: "Onko lattialle asetettu patja vauvalle turvallinen nukkumapaikka?",
        a: "Tavallinen lattialle asetettu patja ei automaattisesti vastaa vauvoille suunnitellun pinnasängyn, kehdon tai muun hyväksytyn nukkumapaikan turvallisuusominaisuuksia. Noudata vauvan nukkumisjärjestelyissä ajantasaisia turvallisen unen suosituksia.",
      },
      {
        q: "Parantaako lattiavuode lapsen unta?",
        a: "Ei itsessään. Lattiavuode ei takaa nopeampaa nukahtamista, vähäisempiä yöheräämisiä tai parempaa unen laatua — lasten uni vaihtelee yksilöllisesti.",
      },
    ],
    sources: [
      "Terveyden ja hyvinvoinnin laitos (THL) sekä neuvolan ohjeet: vauvan turvallinen uni ja nukkumisympäristö.",
      "Turvallisuus- ja kemikaalivirasto (Tukes): lastenhuoneen ja kodin turvallisuutta koskeva ohjeistus.",
      "Nukkumapaikan ja patjan valmistajan käyttöohje — ensisijainen tietolähde tuotekohtaisissa kysymyksissä.",
      "Montessori, M. (1949). The Absorbent Mind — pedagoginen tausta, ei turvallisuusohje.",
    ],
  },
  {
    slug: "montessori-vaatehuolto-opas",
    topic: "arki",
    ageGroups: ["1-2", "2-3", "3-6"],
    tags: ["vaaterekki", "pukeutuminen", "eteinen", "säilytys"],
    updatedISO: "2026-08-16",
    to: "/montessori-koulu/montessori-vaatehuolto-opas",
    category: "itsenaisyys",
    eyebrow: "Itsenäinen pukeutuminen",
    title: "Montessori-vaatehuolto — vaaterekki & itsenäinen pukeutuminen",
    shortTitle: "Vaaterekki & pukeutuminen",
    excerpt:
      "Miten järjestät lapsen vaaterekin niin, että 2-vuotias valitsee itse ja 4-vuotias pukeutuu ilman apua. Ikäopas ja tarkistuslista.",
    minutes: 7,
    age: "1 – 6 v",
    featured: true,
    takeaway:
      "Kun vaatteet ovat lapsen korkeudella ja valikoima on rajattu muutamaan vaihtoehtoon, 2-vuotias valitsee itse ja 4-vuotias pukeutuu kokonaan ilman apua.",
    faqs: [
      {
        q: "Mistä iästä lapsi oppii pukeutumaan itse?",
        a: "Ensimmäiset yritykset näkyvät noin 18 kuukauden iässä, 2-vuotias valitsee ja pukee helpot vaatteet ja 4-vuotias pukeutuu yleensä kokonaan itse. Edellytyksenä on, että vaatteet ovat lapsen ulottuvilla.",
      },
      {
        q: "Kuinka monta vaatetta rekkiin kannattaa laittaa?",
        a: "Rajaa valikoima 3–5 vaihtoehtoon kerrallaan. Liikaa valinnanvaraa kuormittaa lasta — muutama hyvä vaihtoehto tekee valinnasta onnistumisen.",
      },
      {
        q: "Entä jos lapsi valitsee aina saman paidan?",
        a: "Se on normaalia ja jopa hyödyllistä: toisto rakentaa itseluottamusta. Kierrätä vaatteita viikoittain, niin suosikki vaihtuu luontevasti.",
      },
    ],
    sources: [
      "Lillard, A. S. (2017). Montessori: The Science Behind the Genius (3rd ed.). Oxford University Press.",
      "Montessori, M. (1949). The Absorbent Mind.",
    ],
  },
  {
    slug: "montessori-kalusteet-kotiin",
    topic: "kotona",
    ageGroups: ["0-1", "1-2", "2-3", "3-6"],
    tags: ["kalusteet", "lastenhuone", "hyllyt", "aloittaminen"],
    updatedISO: "2026-08-16",
    to: "/montessori-koulu/montessori-kalusteet-kotiin",
    category: "koti",
    eyebrow: "Aloittajan opas",
    title: "Montessori-kalusteet kotiin — mistä aloittaa",
    shortTitle: "Montessori-kalusteet kotiin",
    excerpt: "6-vaiheinen aloitusopas: oppimistorni, lattiasänky, avoimet hyllyt ja Pikler-teline.",
    minutes: 8,
    age: "0 – 6 v",
  },
  {
    slug: "lapsi-keittiossa-ikaopas",
    topic: "arki",
    ageGroups: ["1-2", "2-3", "3-6"],
    tags: ["keittiö", "ruoanlaitto", "oppimistorni", "arjen taidot"],
    updatedISO: "2026-08-16",
    to: "/montessori-koulu/lapsi-keittiossa-ikaopas",
    category: "taapero",
    eyebrow: "Ikäopas",
    title: "Lapsi keittiössä — mihin ikäkausi kykenee",
    shortTitle: "Lapsi keittiössä — ikäopas",
    excerpt:
      "1-vuotias pesee vihanneksia, 3-vuotias leikkaa banaania, 5-vuotias tekee aamiaisen. Konkreettinen ikäopas oppimistornin kanssa.",
    minutes: 7,
    age: "1 – 5 v",
  },
  {
    slug: "kiipeilykolmio-lapselle-ikaopas",
    topic: "ikakaudet",
    ageGroups: ["0-1", "1-2", "2-3", "3-6"],
    tags: ["kiipeilykolmio", "pikler", "ikäopas", "liikkuminen"],
    updatedISO: "2026-08-16",
    to: "/montessori-koulu/kiipeilykolmio-lapselle-ikaopas",
    category: "motoriikka",
    eyebrow: "Ikäopas",
    title: "Kiipeilykolmio lapselle — ikäopas vanhemmalle",
    shortTitle: "Kiipeilykolmio ikäryhmittäin",
    excerpt: "Ikäkohtaiset käyttöideat ja milloin lisätä kaari tai ramppi. Vauvasta esikouluikäiseen.",
    minutes: 7,
    age: "Ikä vaihtelee mallista",
  },
  {
    slug: "vanhempien-tarinoita",
    topic: "kotona",
    ageGroups: ["1-2", "2-3", "3-6"],
    tags: ["arki", "esimerkit", "perhe"],
    to: "/montessori-koulu/vanhempien-tarinoita",
    category: "vanhemman-opas",
    eyebrow: "Esimerkkitarinoita",
    title: "Vanhempien arjen esimerkkejä — Montessori kotona",
    shortTitle: "Vanhempien arjen esimerkkejä",
    excerpt: "Kolme esimerkkitarinaa tavallisesta lapsiperhearjesta: miten Pikler, oppimistorni ja avoimet hyllyt voivat muuttaa päivää.",
    minutes: 6,
  },
  {
    slug: "montessori-historia-suomi",
    topic: "kotona",
    tags: ["historia", "suomi", "päiväkoti"],
    to: "/montessori-koulu/montessori-historia-suomi",
    category: "perusteet",
    eyebrow: "Historia",
    title: "Montessori-pedagogiikan historia Suomessa",
    shortTitle: "Montessori Suomessa — historia",
    excerpt: "Miten Maria Montessorin pedagogiikka rantautui Suomeen, kasvoi päiväkodeissa ja löysi tiensä suomalaisiin koteihin.",
    minutes: 8,
  },
  {
    slug: "hei-koulujarjestelma-historia",
    topic: "kotona",
    tags: ["hei schools", "historia", "varhaiskasvatus"],
    to: "/montessori-koulu/hei-koulujarjestelma-historia",
    category: "perusteet",
    eyebrow: "Historia",
    title: "HEI-koulujärjestelmän historia",
    shortTitle: "HEI-koulujärjestelmän historia",
    excerpt: "HEI Schoolsin synty Helsingistä ja suomalaisen varhaiskasvatuksen tie maailmalle.",
    minutes: 7,
  },
  {
    slug: "maria-montessori-elamakerta",
    topic: "kotona",
    tags: ["maria montessori", "historia", "pedagogiikka"],
    to: "/montessori-koulu/maria-montessori-elamakerta",
    category: "perusteet",
    eyebrow: "Henkilö",
    title: "Maria Montessori — elämäkerta ja pedagogiikan synty",
    shortTitle: "Maria Montessori — elämäkerta",
    excerpt: "Italian ensimmäinen naislääkäri, Casa dei Bambini 1907 ja se, miten yksi havainto muutti kasvatuksen maailmanlaajuisesti.",
    minutes: 9,
  },
  {
    slug: "emmi-pikler-elamakerta",
    topic: "liike",
    tags: ["emmi pikler", "vapaa liike", "historia"],
    to: "/montessori-koulu/emmi-pikler-elamakerta",
    category: "perusteet",
    eyebrow: "Henkilö",
    title: "Emmi Pikler — pediatri ja vapaa liike",
    shortTitle: "Emmi Pikler — elämäkerta",
    excerpt:
      "Unkarilainen lastenlääkäri, joka osoitti että vauva oppii liikkumaan ilman aikuisen ohjausta. Lóczy-koti ja Pikler-kolmion synty.",
    minutes: 8,
  },
  {
    slug: "montessori-hei-vertailu",
    topic: "leikki",
    tags: ["vertailu", "hei schools", "pedagogiikka"],
    to: "/montessori-koulu/montessori-hei-vertailu",
    category: "vanhemman-opas",
    eyebrow: "Vertailu",
    title: "Montessori vs. HEI Schools",
    shortTitle: "Montessori vs. HEI Schools",
    excerpt: "Kaksi suuntausta — missä ne kohtaavat ja erkanevat, ja miten soveltaa niitä kotona.",
    minutes: 8,
  },
  {
    slug: "montessori-waldorf-reggio",
    topic: "leikki",
    tags: ["waldorf", "reggio emilia", "vertailu"],
    to: "/montessori-koulu/montessori-waldorf-reggio",
    category: "vanhemman-opas",
    eyebrow: "Vertailu",
    title: "Montessori vs. Waldorf vs. Reggio Emilia",
    shortTitle: "Montessori, Waldorf ja Reggio",
    excerpt: "Kolme maailman tunnetuinta vaihtoehtopedagogiikkaa vertailussa: juuret, ympäristö, materiaalit ja arjen sovellus.",
    minutes: 9,
  },
];

/** Three next articles after the current one, wrapping around the archive. */
export function relatedPosts(currentSlug: string | undefined, count = 3): Post[] {
  const index = POSTS.findIndex((p) => p.slug === currentSlug);
  if (index === -1) return POSTS.slice(0, count);
  const sameCategory = POSTS.filter((p) => p.slug !== currentSlug && p.category === POSTS[index].category);
  const others = POSTS.filter((p) => p.slug !== currentSlug && p.category !== POSTS[index].category);
  return [...sameCategory, ...others].slice(0, count);
}

/** Learning-path position for an article, if it belongs to a series. */
export function seriesPosition(slug: string | undefined) {
  if (!slug) return undefined;
  for (const [id, series] of Object.entries(SERIES) as [SeriesId, (typeof SERIES)[SeriesId]][]) {
    const i = series.slugs.indexOf(slug);
    if (i === -1) continue;
    const at = (n: number) => POSTS.find((p) => p.slug === series.slugs[n]);
    return {
      id,
      label: series.label,
      part: i + 1,
      total: series.slugs.length,
      previous: i > 0 ? at(i - 1) : undefined,
      next: i < series.slugs.length - 1 ? at(i + 1) : undefined,
    };
  }
  return undefined;
}

/** Free-text search across title, excerpt, topic, tags and age groups. */
export function searchPosts(query: string, posts: ReadonlyArray<Post> = POSTS): Post[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...posts];
  const terms = q.split(/\s+/);
  return posts.filter((p) => {
    const haystack = [
      p.title,
      p.shortTitle,
      p.excerpt,
      p.eyebrow,
      p.age ?? "",
      p.takeaway ?? "",
      ...(p.tags ?? []),
      ...(p.faqs ?? []).map((f) => `${f.q} ${f.a}`),
      TOPICS.find((t) => t.id === p.topic)?.label ?? "",
      CATEGORIES.find((c) => c.id === p.category)?.label ?? "",
      ...(p.ageGroups ?? []).map((a) => AGE_GROUPS.find((g) => g.id === a)?.label ?? ""),
    ]
      .join(" ")
      .toLowerCase();
    return terms.every((t) => haystack.includes(t));
  });
}
